// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE C4 CONTAINER VIEW, IN CODE — a faithful rebuild of the Nano Banana render.
//
// Erez, 2026-07-30, on the image: "the width of the containers are not
// geometrical, they're basically based on what the internal content is. That's
// good, it looks good. Make exactly the same thing."
//
// He named the actual difference. Every React diagram we have built tonight
// hand-places x/y/w/h, so a container's size is a GUESS that has to be nudged
// whenever its contents change — which is why the padding drifted, why boxes
// ended up wider than their text, and why it never looked geometric. The image
// looked right because the model drew containers AROUND their contents.
//
// So this file is not another set of coordinates. It is a tiny LAYOUT ENGINE:
//
//   measure(item)  bottom-up — a leaf's size comes from its own text; a box's
//                  size comes from its children plus gaps, padding and header.
//   place(item)    top-down — walks the tree assigning absolute rects.
//   route(a,b)     edges resolve against the measured rects, so a wire cannot
//                  point at where a box USED to be.
//
// Nothing has a hand-written width. Add a component to a container and the
// container grows, the parent grows, the canvas grows, and every edge still
// lands. That is the whole point, and it is why this is worth more than the
// picture it copies.
//
// C4 conventions kept from the reference: people outside the boundary,
// [technology] in brackets under each container name, a protocol on every wire,
// a title block with a version, and a legend defining the line styles.
//
// Owner agent: Jarvis.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import * as Icons from "lucide-react";

const SANS = "Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** Sampled off the two Nano Banana renders. Erez: "take the colours from the
 *  Nano Banana thing… and also from number two, where you have a blue container
 *  and inside it grey boxes — in the first one the container is blue and the
 *  boxes are blue too, so you don't see it so much."
 *
 *  That is the whole difference between the two modes below. It is not a colour
 *  choice, it is whether a child is allowed to share its parent's value:
 *
 *  CONTRAST — every nesting level flips. Containers alternate slate-blue and
 *    pale grey, and leaves are WHITE with a dark border, so a box always stands
 *    off whatever it sits on. This is the deployment render (number two).
 *  FLAT — one grey family all the way down, separated only by thin borders and
 *    a small step in value. This is the C4 render (number one). Calmer, and it
 *    holds together better when there are many nested levels.
 *
 *  Both are legitimate; they say different things. Contrast makes the CONTENTS
 *  the subject. Flat makes the STRUCTURE the subject. */
type Palette = {
  ink: string; ink2: string; edge: string;
  depth: [string, string, string];   // container fill by nesting level
  depthLine: string;
  leafFill: string; leafLine: string;
  red: string; redFill: string; paper: string;
  titleFill: string;
};

const CONTRAST: Palette = {
  ink: "#2F3E4F", ink2: "#5B6B7C", edge: "#4A5A6B",
  depth: ["#EFF2F6", "#B9C6D8", "#E4EAF1"],
  depthLine: "#7E8EA1",
  leafFill: "#FFFFFF", leafLine: "#4A5A6B",
  red: "#B0463F", redFill: "#F2C9C6", paper: "#FFFFFF",
  titleFill: "#C3CFDE",
};

const FLAT: Palette = {
  ink: "#2F3E4F", ink2: "#5B6B7C", edge: "#4A5A6B",
  depth: ["#F4F6F9", "#E8EDF2", "#DDE4EC"],
  depthLine: "#9AA8B8",
  leafFill: "#EDF0F4", leafLine: "#7E8EA0",
  red: "#B0463F", redFill: "#F2D2D0", paper: "#FFFFFF",
  titleFill: "#FFFFFF",
};
// ─── Text metrics — the engine cannot measure without them ───────────────────
// Approximations, deliberately slightly generous. A box that is 3px too wide is
// invisible; one that is 3px too narrow clips its label, which is the failure
// mode Erez kept catching.
let T: Palette = FLAT;

const wSans = (s: string, px: number, bold = false) => s.length * px * (bold ? 0.585 : 0.545);
const wMono = (s: string, px: number) => s.length * px * 0.601;

// ─── The tree ────────────────────────────────────────────────────────────────

type Tone = "leaf" | "gate";
type Leaf = {
  kind: "leaf"; id: string; name: string; tech?: string; icon?: string; tone?: Tone;
  /** Person notation: icon above a label, no box drawn. C4 draws actors bare. */
  person?: boolean;
};
type Box = {
  kind: "box"; id: string; title?: string; tech?: string;
  dir: "row" | "col"; gap?: number; pad?: number; depth?: 0 | 1 | 2;
  align?: "start" | "center" | "stretch";
  children: Item[];
};
export type Item = Leaf | Box;

export const leaf = (id: string, name: string, tech?: string, icon?: string, tone?: Tone): Leaf =>
  ({ kind: "leaf", id, name, tech, icon, tone });
export const person = (id: string, name: string): Leaf => ({ kind: "leaf", id, name, person: true, icon: "UserRound" });
export const box = (id: string, title: string | undefined, dir: "row" | "col", children: Item[], o: Partial<Box> = {}): Box =>
  ({ kind: "box", id, title, dir, children, gap: 14, pad: 13, depth: 1, ...o });

// ─── Measure — bottom up ─────────────────────────────────────────────────────

const LEAF_PAD_X = 13;
const LEAF_MIN_W = 92;
const ICON = 17;
const HDR = 26;          // container header strip
const PERSON_H = 62;

type Size = { w: number; h: number };
const size = new Map<string, Size>();

function measure(it: Item): Size {
  if (it.kind === "leaf") {
    if (it.person) {
      const s = { w: Math.max(76, wSans(it.name, 12) + 16), h: PERSON_H };
      size.set(it.id, s); return s;
    }
    const nameW = wSans(it.name, 12.5, true) + (it.icon ? ICON + 8 : 0);
    const techW = it.tech ? wMono(it.tech, 10.5) : 0;
    const w = Math.max(LEAF_MIN_W, Math.ceil(Math.max(nameW, techW)) + LEAF_PAD_X * 2);
    const h = it.tech ? 50 : 36;
    const s = { w, h };
    size.set(it.id, s); return s;
  }
  const kids = it.children.map(measure);
  const gap = it.gap ?? 14, pad = it.pad ?? 13;
  const inner = it.dir === "row"
    ? { w: kids.reduce((a, k) => a + k.w, 0) + gap * (kids.length - 1), h: Math.max(...kids.map((k) => k.h)) }
    : { w: Math.max(...kids.map((k) => k.w)), h: kids.reduce((a, k) => a + k.h, 0) + gap * (kids.length - 1) };
  const head = it.title ? HDR : 0;
  // The header must not be clipped either — a title wider than the children
  // widens the container, which is exactly what the reference does.
  const titleW = it.title ? wSans(it.title, 12, true) + (it.tech ? wMono(it.tech, 11) + 8 : 0) + pad * 2 : 0;
  const s = { w: Math.max(inner.w + pad * 2, titleW), h: inner.h + pad * 2 + head };
  size.set(it.id, s); return s;
}

// ─── Place — top down ────────────────────────────────────────────────────────

type Rect = { x: number; y: number; w: number; h: number };
const rect = new Map<string, Rect>();

function place(it: Item, x: number, y: number) {
  const s = size.get(it.id)!;
  rect.set(it.id, { x, y, w: s.w, h: s.h });
  if (it.kind === "leaf") return;
  const gap = it.gap ?? 14, pad = it.pad ?? 13;
  const head = it.title ? HDR : 0;
  let cx = x + pad, cy = y + pad + head;
  const innerH = s.h - pad * 2 - head, innerW = s.w - pad * 2;
  for (const c of it.children) {
    const cs = size.get(c.id)!;
    if (it.dir === "row") {
      place(c, cx, cy + (innerH - cs.h) / 2);   // centred cross-axis, like the reference
      cx += cs.w + gap;
    } else {
      place(c, cx + (innerW - cs.w) / 2, cy);
      cy += cs.h + gap;
    }
  }
}

// ─── Edges — resolved against measured rects ─────────────────────────────────

type Side = "l" | "r" | "t" | "b";
export type Wire = { a: string; b: string; as: Side; bs: Side; label?: string; red?: boolean; dashed?: boolean; num?: number };

const anchor = (id: string, s: Side) => {
  const r = rect.get(id)!;
  return s === "l" ? { x: r.x, y: r.y + r.h / 2 }
    : s === "r" ? { x: r.x + r.w, y: r.y + r.h / 2 }
    : s === "t" ? { x: r.x + r.w / 2, y: r.y }
    : { x: r.x + r.w / 2, y: r.y + r.h };
};

function path(w: Wire) {
  const A = anchor(w.a, w.as), B = anchor(w.b, w.bs);
  const hz = w.as === "l" || w.as === "r";
  const vt = w.as === "t" || w.as === "b";
  if (hz && (w.bs === "l" || w.bs === "r")) {
    const mx = (A.x + B.x) / 2;
    return `M${A.x},${A.y} L${mx},${A.y} L${mx},${B.y} L${B.x},${B.y}`;
  }
  if (vt && (w.bs === "t" || w.bs === "b")) {
    const my = (A.y + B.y) / 2;
    return `M${A.x},${A.y} L${A.x},${my} L${B.x},${my} L${B.x},${B.y}`;
  }
  return hz ? `M${A.x},${A.y} L${B.x},${A.y} L${B.x},${B.y}` : `M${A.x},${A.y} L${A.x},${B.y} L${B.x},${B.y}`;
}


// ─── A diagram is a set of columns, wires and a legend ───────────────────────
//
// Generalised from the single C4 model so the deployment and data-flow views
// run through the SAME engine. Three diagrams, one measure/place, one renderer:
// a new one is a data object, and none of them can drift from the others.

type Col = { root: Item; align?: "center" | "top"; dy?: number };
type Legend = { style: "solid" | "dash" | "red"; label: string };
export type Diagram = {
  key: string; title: string; subtitle: string;
  cols: Col[]; wires: Wire[]; legend: Legend[];
  /** Extra clearance under the tallest column, for legends that sit low. */
  padBottom?: number;
};

const PAD = 30, TITLE_H = 74, COL_GAP = 58;

/** Measure every column, place them left to right, return the canvas size. */
function layout(d: Diagram) {
  const sizes = d.cols.map((c) => measure(c.root));
  const tallest = Math.max(...sizes.map((s) => s.h));
  const top = PAD + TITLE_H;
  let x = PAD;
  d.cols.forEach((c, i) => {
    const s = sizes[i];
    const y = c.align === "top" ? top : top + (tallest - s.h) / 2;
    place(c.root, x, y + (c.dy ?? 0));
    x += s.w + COL_GAP;
  });
  return { w: x - COL_GAP + PAD, h: top + tallest + PAD + (d.padBottom ?? 46) };
}

// ═══ 1 · C4 CONTAINER VIEW ═══════════════════════════════════════════════════

const C4: Diagram = (() => {
  const cli = box("c4-cli", "CLI PROCESSES", "col", [
    box("c4-cliR1", undefined, "row", [leaf("c4-claude", "claude"), leaf("c4-codex", "codex")], { pad: 0, gap: 10 }),
    box("c4-cliR2", undefined, "row", [leaf("c4-pi", "pi"), leaf("c4-acpx", "acpx")], { pad: 0, gap: 10 }),
  ], { gap: 10, depth: 2 });

  const paperclip = box("c4-pc", "PAPERCLIP RUNTIME", "row", [
    leaf("c4-web", "Web App", "[React]", "LayoutDashboard"),
    leaf("c4-api", "API", "[REST + WS]", "Radio"),
    box("c4-pcR", undefined, "col", [
      leaf("c4-auth", "Auth", "[better-auth]", "Users"),
      leaf("c4-reg", "Adapter Registry", undefined, "Cpu"),
    ], { pad: 0, gap: 12 }),
    cli,
  ], { tech: "[Node.js]", depth: 1 });

  const domain = box("c4-dom", "DOMAIN SERVICE", "col", [
    box("c4-d1", undefined, "row", [
      leaf("c4-tgh", "Telegram Handler", undefined, "Send"),
      leaf("c4-cap", "Capture", undefined, "Mic"),
      leaf("c4-run", "Agent Runtime", undefined, "Zap"),
      leaf("c4-wf", "Workflow Engine", undefined, "Workflow"),
    ], { pad: 0, gap: 12 }),
    box("c4-d2", undefined, "row", [
      leaf("c4-tools", "Typed Tools", undefined, "Wrench"),
      leaf("c4-out", "Outbox", undefined, "Inbox"),
    ], { pad: 0, gap: 12 }),
  ], { tech: "[Python/FastAPI]", depth: 1, gap: 12 });

  const gates = box("c4-gates", undefined, "row", [
    leaf("c4-gin", "INBOUND GATE", undefined, "ShieldCheck", "gate"),
    leaf("c4-gout", "OUTBOUND GATE", undefined, "ShieldAlert", "gate"),
  ], { pad: 0, gap: 300 });

  const data = box("c4-dataRow", undefined, "row", [
    box("c4-data", "DATA", "row", [
      leaf("c4-pgp", "schema: paperclip", undefined, "Database"),
      leaf("c4-pgt", "schema: tailormind", undefined, "Database"),
    ], { tech: "[PostgreSQL 16]", depth: 1, gap: 12 }),
    leaf("c4-blob", "Object Storage", "[S3]", "Cloud"),
  ], { pad: 0, gap: 46 });

  return {
    key: "c4", title: "Tailormind — Container View", subtitle: "C4 Level 2 · v1.0",
    cols: [
      { root: box("c4-actors", undefined, "col", [person("c4-staff", "Staff"), person("c4-op", "Field Operator")], { pad: 0, gap: 34 }) },
      { root: box("c4-server", "CLIENT-OWNED SERVER", "col", [paperclip, domain, gates, data], { depth: 0, gap: 26, pad: 18 }), align: "top" },
      { root: box("c4-ext", undefined, "col", [leaf("c4-models", "Model APIs", "[Anthropic]", "Cloud")], { pad: 0 }), dy: -60 },
    ],
    wires: [
      { a: "c4-staff", b: "c4-web", as: "r", bs: "l", label: "HTTPS" },
      { a: "c4-op", b: "c4-web", as: "r", bs: "l", label: "HTTPS" },
      { a: "c4-web", b: "c4-api", as: "r", bs: "l", label: "WSS" },
      { a: "c4-api", b: "c4-auth", as: "r", bs: "l", label: "REST" },
      { a: "c4-api", b: "c4-reg", as: "r", bs: "l", label: "REST" },
      { a: "c4-cli", b: "c4-api", as: "l", bs: "r", label: "stdio" },
      { a: "c4-tgh", b: "c4-cap", as: "r", bs: "l", label: "REST" },
      { a: "c4-cap", b: "c4-run", as: "r", bs: "l", label: "REST" },
      { a: "c4-run", b: "c4-wf", as: "r", bs: "l", label: "REST" },
      { a: "c4-tools", b: "c4-out", as: "r", bs: "l", dashed: true },
      { a: "c4-wf", b: "c4-models", as: "r", bs: "l", label: "REST" },
      { a: "c4-api", b: "c4-gin", as: "b", bs: "t", label: "REST", red: true },
      { a: "c4-cap", b: "c4-gin", as: "b", bs: "t", label: "REST", red: true },
      { a: "c4-run", b: "c4-gout", as: "b", bs: "t", label: "REST", red: true },
      { a: "c4-gin", b: "c4-pgt", as: "b", bs: "t", label: "SQL", red: true },
      { a: "c4-gout", b: "c4-blob", as: "b", bs: "t", label: "REST", red: true },
    ],
    legend: [
      { style: "solid", label: "solid = synchronous" },
      { style: "dash", label: "dashed = async" },
      { style: "red", label: "red = policy enforced" },
    ],
  };
})();

// ═══ 2 · DEPLOYMENT VIEW ═════════════════════════════════════════════════════
// The diagram an ops person asks for: the real box, the real services, the real
// ports. Ports are part of the NAME here — a deployment diagram without them is
// a container diagram wearing a hat.

const DEPLOY: Diagram = (() => {
  const compose = box("dp-compose", "DOCKER COMPOSE", "col", [
    leaf("dp-caddy", "caddy", ":443", "Shield"),
    leaf("dp-pc", "paperclip", ":3000", "Server"),
    leaf("dp-api", "domain-api", ":8000", "Server"),
    leaf("dp-pg", "postgres", ":5432", "Database"),
    leaf("dp-whisper", "whisper", ":9000", "AudioLines"),
  ], { depth: 1, gap: 12 });

  const side = box("dp-side", undefined, "col", [
    leaf("dp-systemd", "systemd", undefined, "Terminal"),
    leaf("dp-backups", "Backups", "nightly", "Archive"),
  ], { pad: 0, gap: 26 });

  return {
    key: "dp", title: "Tailormind — Deployment View", subtitle: "Single-tenant · v1.0",
    cols: [
      { root: box("dp-clients", undefined, "col", [
          leaf("dp-browser", "Staff browser", undefined, "LayoutDashboard"),
          leaf("dp-mobile", "Mobile", undefined, "Smartphone"),
        ], { pad: 0, gap: 40 }) },
      { root: box("dp-vps", "VPS · 4 vCPU / 8 GB · HETZNER", "row", [compose, side], { depth: 0, pad: 18, gap: 44 }), align: "top" },
      { root: box("dp-ext", undefined, "col", [
          leaf("dp-models", "Model APIs", undefined, "Cloud"),
          leaf("dp-obj", "Object Storage", undefined, "Cloud"),
          leaf("dp-tg", "Telegram API", undefined, "Send"),
        ], { pad: 0, gap: 34 }) },
    ],
    wires: [
      { a: "dp-browser", b: "dp-caddy", as: "r", bs: "l", label: "TLS 1.3", dashed: true },
      { a: "dp-mobile", b: "dp-caddy", as: "r", bs: "l", label: "TLS 1.3", dashed: true },
      { a: "dp-caddy", b: "dp-pc", as: "b", bs: "t", label: "HTTP" },
      { a: "dp-pc", b: "dp-api", as: "b", bs: "t", label: "HTTP" },
      { a: "dp-api", b: "dp-pg", as: "b", bs: "t", label: "TCP 5432" },
      { a: "dp-api", b: "dp-whisper", as: "l", bs: "l", label: "gRPC" },
      { a: "dp-systemd", b: "dp-compose", as: "l", bs: "r" },
      { a: "dp-backups", b: "dp-pg", as: "l", bs: "r", dashed: true },
      { a: "dp-api", b: "dp-models", as: "r", bs: "l", label: "HTTPS", dashed: true },
      { a: "dp-pc", b: "dp-obj", as: "r", bs: "l", label: "HTTPS", dashed: true },
      { a: "dp-tg", b: "dp-api", as: "l", bs: "r", label: "HTTPS", dashed: true },
    ],
    legend: [
      { style: "solid", label: "solid = in-process" },
      { style: "dash", label: "dashed = network" },
      { style: "red", label: "red = trust boundary" },
    ],
  };
})();

// ═══ 3 · VOICE CAPTURE DATA FLOW ═════════════════════════════════════════════
// One request end to end. The numbers ARE the diagram — without them this is
// just a pipeline, and the reference image made that point by putting a small
// numbered disc on every hop.

const FLOW: Diagram = (() => {
  const stage = (id: string, title: string, kids: Item[]) =>
    box(id, title, "row", kids, { depth: 1, gap: 12 });

  const top = box("fl-top", undefined, "row", [
    stage("fl-s1", "EXTERNAL", [leaf("fl-op", "Field Operator", undefined, "UserRound"), leaf("fl-tg", "Telegram API", undefined, "Send")]),
    stage("fl-s2", "INGESTION", [leaf("fl-hook", "Webhook Handler", undefined, "Radio")]),
    stage("fl-s3", "PROCESSING", [
      leaf("fl-stt", "Transcriber", "[Whisper]", "AudioLines"),
      leaf("fl-ext", "Field Extractor", undefined, "ListChecks"),
      leaf("fl-conf", "Confidence Scorer", undefined, "Gauge"),
    ]),
    leaf("fl-gin", "INBOUND GATE", undefined, "ShieldCheck", "gate"),
    stage("fl-s4", "STORAGE", [leaf("fl-pgt", "schema: tailormind", undefined, "Database")]),
    stage("fl-s5", "INTERFACE", [leaf("fl-card", "Confirmation Card", undefined, "LayoutDashboard")]),
  ], { pad: 0, gap: 26 });

  const bottom = box("fl-bot", undefined, "row", [
    stage("fl-s6", "RESPONSE", [leaf("fl-run", "Agent Runtime", undefined, "Zap")]),
    leaf("fl-gout", "OUTBOUND GATE", undefined, "ShieldAlert", "gate"),
    stage("fl-s7", "MODELS", [leaf("fl-gw", "Model Gateway", undefined, "GitFork"), leaf("fl-models", "Model APIs", undefined, "Cloud")]),
  ], { pad: 0, gap: 26 });

  return {
    key: "fl", title: "Tailormind — Voice Capture Data Flow", subtitle: "Sequence · v1.0",
    padBottom: 70,
    cols: [
      { root: box("fl-all", undefined, "col", [top, bottom], { pad: 0, gap: 58 }), align: "top" },
      { root: box("fl-audit", undefined, "col", [leaf("fl-log", "Audit Log", "append-only", "ScrollText")], { pad: 0 }) },
    ],
    wires: [
      { a: "fl-op", b: "fl-tg", as: "r", bs: "l", label: "voice note", num: 1 },
      { a: "fl-tg", b: "fl-hook", as: "r", bs: "l", label: "webhook", num: 2 },
      { a: "fl-hook", b: "fl-stt", as: "r", bs: "l", label: "wav", num: 3 },
      { a: "fl-stt", b: "fl-ext", as: "r", bs: "l", label: "JSON", num: 4 },
      { a: "fl-ext", b: "fl-conf", as: "r", bs: "l", label: "score", num: 5 },
      { a: "fl-conf", b: "fl-gin", as: "r", bs: "l", label: "commit", num: 6, red: true },
      { a: "fl-gin", b: "fl-pgt", as: "r", bs: "l", label: "SQL", num: 7, red: true },
      { a: "fl-pgt", b: "fl-card", as: "r", bs: "l", label: "render", num: 8 },
      { a: "fl-run", b: "fl-gout", as: "r", bs: "l", label: "RPC", num: 9, red: true },
      { a: "fl-gout", b: "fl-gw", as: "r", bs: "l", red: true },
      { a: "fl-gw", b: "fl-models", as: "r", bs: "l" },
      { a: "fl-gin", b: "fl-log", as: "b", bs: "l", dashed: true },
      { a: "fl-gout", b: "fl-log", as: "b", bs: "l", dashed: true },
    ],
    legend: [
      { style: "solid", label: "solid = data path" },
      { style: "dash", label: "dashed = audit" },
      { style: "red", label: "red = policy enforced" },
    ],
  };
})();

// ─── Render ──────────────────────────────────────────────────────────────────

function Ico({ name, size: sz, color }: { name: string; size: number; color: string }) {
  const I = (Icons as unknown as Record<string, React.FC<{ size?: number; color?: string; strokeWidth?: number }>>)[name] ?? Icons.Box;
  return <I size={sz} color={color} strokeWidth={1.7} />;
}

function drawBox(it: Item): React.ReactNode {
  const r = rect.get(it.id)!;
  if (it.kind === "leaf") {
    if (it.person) {
      return (
        <div key={it.id} style={{ position: "absolute", left: r.x, top: r.y, width: r.w, height: r.h, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5 }}>
          <Ico name={it.icon ?? "UserRound"} size={30} color={T.ink} />
          <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, color: T.ink, whiteSpace: "nowrap" }}>{it.name}</div>
        </div>
      );
    }
    const gate = it.tone === "gate";
    return (
      <div key={it.id} style={{
        position: "absolute", left: r.x, top: r.y, width: r.w, height: r.h,
        background: gate ? T.redFill : T.leafFill,
        border: `1px solid ${gate ? T.red : T.leafLine}`, borderRadius: 3,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
        boxSizing: "border-box",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {it.icon && <Ico name={it.icon} size={ICON} color={gate ? T.red : T.ink} />}
          <span style={{ fontFamily: SANS, fontSize: 12.5, fontWeight: 700, color: gate ? T.red : T.ink, whiteSpace: "nowrap" }}>{it.name}</span>
        </div>
        {it.tech && <span style={{ fontFamily: MONO, fontSize: 10.5, color: T.ink2, whiteSpace: "nowrap" }}>{it.tech}</span>}
      </div>
    );
  }
  const shell = it.title ? (
    <div key={it.id} style={{
      position: "absolute", left: r.x, top: r.y, width: r.w, height: r.h,
      background: T.depth[it.depth ?? 1],
      border: `1px solid ${T.depthLine}`, borderRadius: 4, boxSizing: "border-box",
    }}>
      <div style={{ height: HDR, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
        <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 800, letterSpacing: ".04em", color: T.ink, whiteSpace: "nowrap" }}>{it.title}</span>
        {it.tech && <span style={{ fontFamily: MONO, fontSize: 11, color: T.ink2, whiteSpace: "nowrap" }}>{it.tech}</span>}
      </div>
    </div>
  ) : null;
  return <React.Fragment key={it.id}>{shell}{it.children.map(drawBox)}</React.Fragment>;
}

export function Canvas({ d, mode }: { d: Diagram; mode: "contrast" | "flat" }) {
  T = mode === "contrast" ? CONTRAST : FLAT;
  const { w: CW, h: CH } = layout(d);
  return (
    <div style={{ position: "relative", width: CW, height: CH, background: T.paper, border: `1px solid ${T.depthLine}` }}>
      <div style={{ position: "absolute", left: PAD, top: PAD, border: `1.5px solid ${T.ink}`, background: T.titleFill }}>
        <div style={{ fontFamily: MONO, fontSize: 16, color: T.ink, padding: "7px 14px", borderBottom: `1.5px solid ${T.ink}` }}>{d.title}</div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: T.ink2, padding: "5px 14px" }}>{d.subtitle}</div>
      </div>

      <svg width={CW} height={CH} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <defs>
          {[["k", T.edge], ["r", T.red]].map(([k, c]) => (
            <marker key={k} id={`${d.key}-${k}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7 z" fill={c as string} />
            </marker>
          ))}
        </defs>
        {d.wires.map((w, i) => {
          const A = anchor(w.a, w.as), B = anchor(w.b, w.bs);
          const c = w.red ? T.red : T.edge;
          const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
          return (
            <g key={i}>
              <path d={path(w)} fill="none" stroke={c} strokeWidth={1.2}
                strokeDasharray={w.dashed ? "5 4" : undefined} markerEnd={`url(#${d.key}-${w.red ? "r" : "k"})`} />
              {w.label && <text x={mx} y={my - (w.num ? 12 : 4)} fontSize={9.5} fontFamily={MONO} fill={c} textAnchor="middle">{w.label}</text>}
              {w.num && (
                <>
                  <circle cx={mx} cy={my + 9} r={7.5} fill={T.paper} stroke={c} strokeWidth={1} />
                  <text x={mx} y={my + 12.5} fontSize={9} fontFamily={MONO} fill={c} textAnchor="middle">{w.num}</text>
                </>
              )}
            </g>
          );
        })}
      </svg>

      {d.cols.map((c) => drawBox(c.root))}

      <div style={{
        position: "absolute", right: PAD, bottom: PAD - 14,
        border: `1px solid ${T.depthLine}`, background: T.paper, padding: "7px 11px",
        display: "flex", flexDirection: "column", gap: 4,
      }}>
        {d.legend.map((l) => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="30" height="8">
              <path d="M0,4 L26,4" stroke={l.style === "red" ? T.red : T.edge} strokeWidth={1.3}
                strokeDasharray={l.style === "dash" ? "4 3" : undefined} markerEnd={`url(#${d.key}-k)`} />
            </svg>
            <span style={{ fontFamily: SANS, fontSize: 10.5, color: l.style === "red" ? T.red : T.ink2, whiteSpace: "nowrap" }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const HEAD: React.CSSProperties = { fontSize: 15, fontWeight: 750, color: "#2F3E4F", marginBottom: 10 };

export function C4Diagram() {
  return (
    <div style={{ fontFamily: SANS }}>
      <div style={{ fontSize: 12.5, color: "#5B6B7C", marginBottom: 22, maxWidth: 1060, lineHeight: 1.6 }}>
        All three Nano Banana enterprise views, rebuilt in code on one layout engine in the house standard —{" "}
        <strong style={{ color: "#2F3E4F" }}>contrast</strong>. Nothing has a hand-written width: a leaf measures its own
        text, a container measures its children, the canvas measures the containers. A fourth diagram is a data object,
        not new code.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
        <div><div style={HEAD}>1 · Container view (C4 Level 2)</div><Canvas d={C4} mode="contrast" /></div>
        <div><div style={HEAD}>2 · Deployment view</div><Canvas d={DEPLOY} mode="contrast" /></div>
        <div><div style={HEAD}>3 · Voice capture data flow</div><Canvas d={FLOW} mode="contrast" /></div>
        <div>
          <div style={HEAD}>The flat alternative, kept for comparison — structure as the subject rather than contents</div>
          <Canvas d={C4} mode="flat" />
        </div>
      </div>
    </div>
  );
}
